'use client'

import React, { useMemo } from "react";
import { useTableMapState } from "../TableMapState";
import { formatValue } from "@/migrated-widgets/table-map/utils/random";
import { groupSalesDataByZipCodeNew, groupSalesDataByZipCodeUsed } from "@/migrated-widgets/table-map/utils/grouping";
import { InventoryRow, InventoryCell } from "./components";
import classNames from "classnames";

interface ZipCodesViewProps {
  vehicleType: "NEW" | "USED";
}

const ZipCodesView: React.FC<ZipCodesViewProps> = ({ vehicleType }) => {
  const {
    transactions, loadingTransactions, dealers,
    showingTransactionType, zipCodes: activeZipCodes, setZipCodes,
    showCrmLeads, totalLeads, circleZipCodes,
  } = useTableMapState();

  const zipCodeRows = useMemo(() => {
    if (loadingTransactions) return [];
    const salesTransactions = transactions.filter((t) => t.transactionType === showingTransactionType);
    let filtered = dealers.length > 0 ? salesTransactions.filter((t) => !dealers.includes(t.clientName)) : salesTransactions;
    if (circleZipCodes.length > 0) {
      filtered = filtered.filter((t) => circleZipCodes.includes(t.zipcode));
    }
    return vehicleType === "NEW" ? groupSalesDataByZipCodeNew(filtered) : groupSalesDataByZipCodeUsed(filtered);
  }, [transactions, loadingTransactions, dealers, showingTransactionType, vehicleType, circleZipCodes]);

  const zipCodeTotal = zipCodeRows.reduce((acc, row) => acc + row.totalSold, 0);

  const handleSelectZipCode = (zipCode: string) => {
    setZipCodes((prev) => {
      if (prev.includes(zipCode)) return prev.filter((z) => z !== zipCode);
      return [...prev, zipCode]
    });
  };

  return (
    <>
      <InventoryRow className="sticky z-10 top-0 bg-[#f9fafa] font-bold">
        <InventoryCell>Zip Code</InventoryCell>
        <InventoryCell>Total Sold</InventoryCell>
      </InventoryRow>

      {loadingTransactions ? (
        <InventoryRow>
          <InventoryCell className="text-table-map-gray text-sm">Loading...</InventoryCell>
        </InventoryRow>
      ) : zipCodeRows.map((row) => (
        <InventoryRow
          key={row.zipCode}
          useCursorPointer
          isHighlighted={activeZipCodes.includes(row.zipCode)}
          onClick={() => handleSelectZipCode(row.zipCode)}
        >
          <InventoryCell>{row.zipCode}</InventoryCell>
          <InventoryCell>{formatValue(row.totalSold)}</InventoryCell>
        </InventoryRow>
      ))}

      <div className={classNames('sticky bottom-0 left-0 w-full')}>
        <InventoryRow className={classNames('font-bold w-full bottom-0 bg-table-map-teal', {
          'rounded-bl-lg': !showCrmLeads,
        })}>
          <InventoryCell className="pl-0 text-white">Total</InventoryCell>
          <InventoryCell className="text-white">{formatValue(zipCodeTotal)}</InventoryCell>
        </InventoryRow>

        {showCrmLeads && (
          <InventoryRow className="font-bold bg-white rounded-bl-xl">
            <InventoryCell className="pl-0 text-[#007BFF]">CRM Leads</InventoryCell>
            <InventoryCell className="text-[#007BFF]">{formatValue(totalLeads)}</InventoryCell>
          </InventoryRow>
        )}
      </div>
    </>
  );
};

export default ZipCodesView;
